import React, { useRef, useCallback, useState, memo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import i18next from 'i18next';
import { useMediaQuery } from 'react-responsive';
import getValidationErrors from '../../../utils/getValidationErrors';

import {
  EmailInput,
  FuzzyLogoNewOrange,
  LockIcon,
} from '../../../assets/svg/SVGComponents';

import Input from '../../../components/Input';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';

import { useAuth } from '../../../hooks/auth';

import {
  Container,
  Content,
  SideContent,
  SignInArea,
  TipBackground,
  ImageArea,
  LanguageButtons,
  TopContent,
  AuthTabs,
  AuthTab,
  MagicLinkSuccess,
} from './styles';

interface ISignInFormData {
  email: string;
  password: string;
}

type AuthMode = 'credentials' | 'magic-link';

const SignIn: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const magicLinkFormRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('magic-link');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const { t } = useTranslation();
  const { signIn, sendMagicLink } = useAuth();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [isMobile, setIsMobile] = useState(false);

  // Obtém a URL de redirecionamento do estado da navegação
  const from = (location.state as { from?: string })?.from || '/home';

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);

  const handleSubmit = useCallback(
    async (data: ISignInFormData) => {
      formRef.current?.setErrors({});

      try {
        setIsLoading(true);
        const signInSchema = Yup.object().shape({
          email: Yup.string()
            .required(t('yup_errors.email_required'))
            .email(t('yup_errors.invalid_email')),
          password: Yup.string().required(t('yup_errors.password_required')),
        });

        await signInSchema.validate(data, {
          abortEarly: false,
        });

        await signIn({
          email: data.email,
          password: data.password,
        });

        // Redireciona para a URL original ou para /home
        navigate(from, { replace: true });

        // navigate('/teachers/home');
      } catch (err) {
        setIsLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, signIn, t, from],
  );

  const handleLanguageChange = useCallback((lang_code: string) => {
    i18next.changeLanguage(lang_code);
  }, []);

  const handleMagicLinkSubmit = useCallback(
    async (data: { email: string }) => {
      magicLinkFormRef.current?.setErrors({});
      try {
        const schema = Yup.object().shape({
          email: Yup.string()
            .required(t('yup_errors.email_required'))
            .email(t('yup_errors.invalid_email')),
        });
        await schema.validate(data, { abortEarly: false });
        setIsLoading(true);
        const sent = await sendMagicLink(data.email);

        if (sent === 'success') setMagicLinkSent(true);

        setMagicLinkEmail(data.email);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          magicLinkFormRef.current?.setErrors(errors);
        }
        if (err instanceof Error) {
          // Aqui você pode adicionar um toast de erro ou alguma forma de feedback para o usuário
          setMagicLinkSent(false);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [sendMagicLink, t],
  );

  return (
    <Container>
      <Content $isMobile={isMobile}>
        <FuzzyLogoNewOrange />
        <AuthTabs>
          <AuthTab
            type="button"
            $active={authMode === 'magic-link'}
            onClick={() => {
              setAuthMode('magic-link');
              setMagicLinkSent(false);
            }}
          >
            Entrar sem senha
          </AuthTab>
          <AuthTab
            type="button"
            $active={authMode === 'credentials'}
            onClick={() => {
              setAuthMode('credentials');
              setMagicLinkSent(false);
            }}
          >
            Email e Senha
          </AuthTab>
        </AuthTabs>

        {authMode === 'credentials' && (
          <Form ref={formRef} onSubmit={handleSubmit}>
            <h1>
              <Trans i18nKey="h1.signin" />
            </h1>

            <span>{t('labels.email')}</span>
            <Input
              name="email"
              type="text"
              placeholder={t('placeholders.email')}
              icon={EmailInput}
              autoComplete="email"
            />

            <span>{t('labels.password')}</span>
            <Input
              name="password"
              placeholder={t('placeholders.password')}
              type="password"
              icon={LockIcon}
              hasToggleButton
              autoComplete="current-password"
            />

            <PrimaryButton autoFocus type="submit" loading={isLoading}>
              {t('buttons.signin')}
            </PrimaryButton>
            <SignInArea>
              <Link to="/forgot-password">{t('navlinks.forgot_password')}</Link>
              <Link to="/signup">{t('navlinks.signup')}</Link>
            </SignInArea>
          </Form>
        )}

        {authMode === 'magic-link' && !magicLinkSent && (
          <Form ref={magicLinkFormRef} onSubmit={handleMagicLinkSubmit}>
            <h1>Entrar Sem senha</h1>
            <p className="magic-link-description">
              Digite seu email e enviaremos um link de acesso direto para a sua
              caixa de entrada.
            </p>

            <span>{t('labels.email')}</span>
            <Input
              name="email"
              type="text"
              placeholder={t('placeholders.email')}
              icon={EmailInput}
              autoComplete="email"
            />

            <PrimaryButton autoFocus type="submit" loading={isLoading}>
              Enviar Email
            </PrimaryButton>
            <SignInArea>
              <Link to="/signup">{t('navlinks.signup')}</Link>
            </SignInArea>
          </Form>
        )}

        {authMode === 'magic-link' && magicLinkSent && (
          <MagicLinkSuccess>
            <span className="success-icon">✉️</span>
            <h2>Link enviado!</h2>
            <p>
              Enviamos um link de acesso para <strong>{magicLinkEmail}</strong>.
              Verifique sua caixa de entrada e clique no link para entrar.
            </p>
            <button
              type="button"
              className="resend-link"
              onClick={() => setMagicLinkSent(false)}
            >
              Reenviar link
            </button>
          </MagicLinkSuccess>
        )}

        <footer />
      </Content>
      {!isMobile && (
        <SideContent>
          <ImageArea>
            <img
              src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/backgroundSignIn.jpg"
              alt="fuzzy-logo"
            />
            <TopContent>
              <LanguageButtons>
                <button
                  type="button"
                  className={
                    i18next.language === 'pt-BR' ? 'active-language' : ''
                  }
                  onClick={() => handleLanguageChange('pt-BR')}
                >
                  PT
                </button>
                |
                <button
                  type="button"
                  className={i18next.language === 'en' ? 'active-language' : ''}
                  onClick={() => handleLanguageChange('en')}
                >
                  EN
                </button>
              </LanguageButtons>
              <TipBackground>
                <img
                  src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/backgroundTip.png"
                  alt="tip-bg"
                />
              </TipBackground>
            </TopContent>
          </ImageArea>
        </SideContent>
      )}
    </Container>
  );
};

export default memo(SignIn);
